import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Setup axios interceptor ONCE (module level, not component level)
let interceptorSetup = false;
function setupInterceptor() {
  if (interceptorSetup) return;
  interceptorSetup = true;
  
  axios.interceptors.request.use((config) => {
    // Don't attach token to refresh or login/register calls
    const skipUrls = ['/auth/refresh', '/auth/login', '/auth/register'];
    const shouldSkip = skipUrls.some(u => config.url?.includes(u));
    
    if (!shouldSkip) {
      const token = localStorage.getItem('access_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
    }
    return config;
  });
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const isAuthenticating = useRef(false);

  // Setup interceptor once
  useEffect(() => {
    setupInterceptor();
  }, []);

  const checkAuth = useCallback(async () => {
    // Don't check if we're in the middle of login/register
    if (isAuthenticating.current) return;
    
    const token = localStorage.getItem('access_token');
    if (!token) {
      setUser(false);
      setLoading(false);
      return;
    }
    try {
      const response = await axios.get(`${API_URL}/api/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      if (error.response?.status === 401) {
        const rt = localStorage.getItem('refresh_token');
        if (rt) {
          try {
            const refreshRes = await axios.post(`${API_URL}/api/auth/refresh`, { refresh_token: rt });
            if (refreshRes.data?.access_token) {
              localStorage.setItem('access_token', refreshRes.data.access_token);
              setUser(refreshRes.data);
              setLoading(false);
              return;
            }
          } catch {
            // Refresh failed — clear tokens
          }
        }
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
      }
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    isAuthenticating.current = true;
    try {
      const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
      const data = response.data;
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      setUser(data);
      setLoading(false);
      return data;
    } finally {
      isAuthenticating.current = false;
    }
  };

  const register = async (email, password, name) => {
    isAuthenticating.current = true;
    try {
      const response = await axios.post(`${API_URL}/api/auth/register`, { email, password, name });
      const data = response.data;
      if (data.access_token) {
        localStorage.setItem('access_token', data.access_token);
      }
      if (data.refresh_token) {
        localStorage.setItem('refresh_token', data.refresh_token);
      }
      setUser(data);
      setLoading(false);
      return data;
    } finally {
      isAuthenticating.current = false;
    }
  };

  const logout = async () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(false);
    try {
      await axios.post(`${API_URL}/api/auth/logout`);
    } catch {}
  };

  const refreshUser = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
